import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import {
  useMonitor,
  useMonitorOptions,
  usePauseMonitor,
  useResumeMonitor,
  useArchiveMonitor,
  useDeleteMonitor,
  usePatchMonitor,
  useMonitorMatches,
} from "@/monitors/api";
import { IRForm } from "@/monitors/components/IRForm";
import { ThresholdSliders, type ThresholdValue } from "@/monitors/components/ThresholdSliders";
import { MatchCard } from "@/monitors/components/MatchCard";
import { AutoPausedBanner } from "@/monitors/components/AutoPausedBanner";
import { useDebouncedCallback } from "@/monitors/hooks/useDebouncedCallback";
import type { MonitorIR } from "@/monitors/schemas";

export function MonitorDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { data: monitor, isLoading, isError } = useMonitor(id);
  const { data: options } = useMonitorOptions();
  const pause = usePauseMonitor();
  const resume = useResumeMonitor();
  const archive = useArchiveMonitor();
  const del = useDeleteMonitor();
  const patch = usePatchMonitor();
  const [tab, setTab] = useState("confirmed");

  // Debounce write-path mutations: IRForm fires `onChange` per chip add/remove
  // and ThresholdSliders fires per drag tick. Without debouncing a single
  // slider drag fires ~25 PATCH calls and every IR chip edit writes a fresh
  // monitor_audit row. Collapsing to one PATCH every 500ms keeps the audit
  // log informative and the network quiet.
  //
  // Hooks before the early return so the call order stays stable.
  const debouncedPatchIR = useDebouncedCallback(
    (ir: MonitorIR) => patch.mutate({ id, patch: { ir } }),
    500,
  );
  const debouncedPatchThresholds = useDebouncedCallback(
    (p: Partial<ThresholdValue>) => patch.mutate({ id, patch: p }),
    500,
  );

  if (isLoading) return <div className="p-6">Loading…</div>;
  if (isError || !monitor) return <div className="p-6">Couldn't load this monitor.</div>;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4">
      <button className="text-sm text-muted-foreground" onClick={() => navigate("/monitors")}>
        ← Monitors
      </button>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{monitor.canonical_statement}</h1>
          <p className="text-sm text-muted-foreground italic mt-1">"{monitor.statement}"</p>
        </div>
        <div className="flex gap-2">
          {monitor.status === "active" && (
            <Button variant="outline" onClick={() => pause.mutate(id)}>Pause</Button>
          )}
          {monitor.status === "paused" && (
            <Button variant="outline" onClick={() => resume.mutate(id)}>Resume</Button>
          )}
          <Button variant="outline" onClick={() => archive.mutate(id)}>Archive</Button>
          <Button
            variant="destructive"
            onClick={() => {
              if (confirm("Delete this monitor?")) {
                del.mutate(id);
                navigate("/monitors");
              }
            }}
          >
            Delete
          </Button>
        </div>
      </div>

      {monitor.status === "paused" && (
        <AutoPausedBanner
          monitor={monitor}
          reason="This monitor is paused."
          onResume={() => resume.mutate(id)}
        />
      )}

      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold mb-3">Compiled understanding</h3>
          <IRForm
            value={monitor.ir}
            onChange={debouncedPatchIR}
            categories={options?.categories ?? []}
            materials={options?.materials ?? []}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold mb-3">Thresholds</h3>
          <ThresholdSliders
            value={monitor}
            onChange={debouncedPatchThresholds}
          />
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="confirmed">Confirmed</TabsTrigger>
          <TabsTrigger value="candidate">Candidates</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
          <TabsTrigger value="backtest">Backtest</TabsTrigger>
        </TabsList>
        <TabsContent value="confirmed" className="mt-4">
          <MatchesTab monitorId={id} stage="confirmed" />
        </TabsContent>
        <TabsContent value="candidate" className="mt-4">
          <MatchesTab monitorId={id} stage="candidate" />
        </TabsContent>
        <TabsContent value="rejected" className="mt-4">
          <MatchesTab monitorId={id} stage="rejected" />
        </TabsContent>
        <TabsContent value="backtest" className="mt-4">
          <p className="text-sm text-muted-foreground">
            Backtest unavailable on existing monitors.{" "}
            <button
              className="underline"
              onClick={() => navigate("/monitors/new")}
            >
              Create a new monitor
            </button>{" "}
            to run a fresh backtest.
          </p>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MatchesTab({ monitorId, stage }: { monitorId: string; stage: "confirmed" | "candidate" | "rejected" }) {
  const { data, isLoading, isError } = useMonitorMatches(monitorId, { stage });
  if (isLoading) return <p className="text-muted-foreground">Loading…</p>;
  if (isError) return <p className="text-red-600">Couldn't load matches.</p>;
  const matches = data?.matches ?? [];
  if (matches.length === 0) return <p className="text-muted-foreground">No {stage} matches.</p>;
  return (
    <ul className="space-y-2">
      {matches.map((m) => (
        <li key={m.id}>
          <MatchCard match={m} monitorName="" size="md" showWhy showThumbs={stage === "confirmed"} />
        </li>
      ))}
    </ul>
  );
}
